import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRoles, useCreateRole } from '../../../platform/api/hooks';
import { asList, asRecord, str } from '../../../platform/api/utils';
import { roleDisplayName } from '../../../shared/labels';
import { PageHeader } from '../../../components/ui/PageHeader';
import { Badge } from '../../../components/ui/Badge';
import { Section } from '../../../components/ui/Section';

export function RolesList() {
  const navigate = useNavigate();
  const { data: roles, isLoading, isError } = useRoles();
  const createRole = useCreateRole();
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [showForm, setShowForm] = useState(false);

  if (isError) {
    return (
      <div style={{ padding: '2.25rem 2.5rem', maxWidth: 1000 }}>
        <PageHeader title="Permissões de acesso" subtitle="Controle de acesso digital da casa" />
        <Section title="Erro">
          <p style={{ color: 'var(--danger)', margin: 0 }}>Não foi possível carregar os papéis.</p>
        </Section>
      </div>
    );
  }

  const list = asList(roles);

  return (
    <div style={{ padding: '2.25rem 2.5rem', maxWidth: 1000 }}>
      <PageHeader
        title="Permissões de acesso"
        subtitle={`${String(list.length)} papéis digitais configurados`}
        action={{
          label: '+ Novo papel',
          onClick: () => {
            setShowForm(true);
          },
        }}
      />

      <Section title="Diferença em relação a cargos">
        <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--muted)', lineHeight: 1.5 }}>
          Aqui você define <strong>papéis digitais</strong> e permissões no sistema. Cargos
          religiosos ou operacionais da casa ficam em <strong>Cargos e funções</strong> e{' '}
          <strong>nunca</strong> liberam acesso automaticamente.
        </p>
      </Section>

      {isLoading && <Skeleton />}

      {!isLoading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {showForm && (
            <Section title="Novo papel">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <input
                  value={newName}
                  onChange={(e) => {
                    setNewName(e.target.value);
                  }}
                  placeholder="Nome do papel..."
                  style={inputCss}
                />
                <input
                  value={newDesc}
                  onChange={(e) => {
                    setNewDesc(e.target.value);
                  }}
                  placeholder="Descrição (opcional)"
                  style={inputCss}
                />
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                    }}
                    style={secondaryBtn}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!newName.trim()) return;
                      createRole.mutate({
                        name: newName.trim(),
                        description: newDesc.trim() || undefined,
                      });
                      setNewName('');
                      setNewDesc('');
                      setShowForm(false);
                    }}
                    disabled={createRole.isPending || !newName.trim()}
                    style={primaryBtn}
                  >
                    {createRole.isPending ? 'Criando...' : 'Criar'}
                  </button>
                </div>
              </div>
            </Section>
          )}

          {list.length === 0 && !showForm && (
            <Section title="Papéis">
              <p style={{ color: 'var(--faint)', textAlign: 'center', padding: '2rem 0' }}>
                Nenhum papel configurado. Crie o primeiro para começar.
              </p>
            </Section>
          )}

          {list.map((raw) => {
            const role = asRecord(raw);
            const isSystem = !!role.isSystem;
            const permList = asList(role.permissions);
            const assignList = asList(role.assignments);

            return (
              <div
                key={str(role.id)}
                onClick={() => {
                  void navigate(`/permissoes/${String(role.id)}`);
                }}
                style={{
                  background: '#fff',
                  borderRadius: 14,
                  border: '1px solid var(--line)',
                  padding: '1rem 1.25rem',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.15s, border-color 0.15s',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = 'var(--green)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = 'var(--line)';
                }}
              >
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                        {roleDisplayName(role.name)}
                      </span>
                      {isSystem && <Badge label="Sistema" variant="info" />}
                    </div>
                    <p style={{ fontSize: '0.8rem', color: 'var(--muted)', margin: '0.25rem 0 0' }}>
                      {str(role.description, 'Sem descrição')}
                    </p>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: '1.5rem',
                      fontSize: '0.8rem',
                      color: 'var(--muted)',
                    }}
                  >
                    <span>{permList.length} permissões</span>
                    <span>{assignList.length} pessoas</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div
        style={{
          marginTop: '1.5rem',
          padding: '0.75rem',
          background: 'var(--paper)',
          borderRadius: 10,
          border: '1px solid var(--line-soft)',
          fontSize: '0.75rem',
          color: 'var(--muted)',
        }}
      >
        Cargo religioso não concede acesso digital automaticamente. As permissões abaixo controlam
        apenas o uso do sistema.
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          style={{
            background: '#fff',
            borderRadius: 14,
            border: '1px solid var(--line)',
            padding: '1rem 1.25rem',
          }}
        >
          <div
            style={{
              width: '30%',
              height: 14,
              background: 'var(--line-soft)',
              borderRadius: 8,
              marginBottom: '0.5rem',
            }}
          />
          <div style={{ width: '50%', height: 12, background: 'var(--soft)', borderRadius: 8 }} />
        </div>
      ))}
    </div>
  );
}

const inputCss: React.CSSProperties = {
  padding: '0.5rem 0.6rem',
  border: '1px solid var(--line)',
  borderRadius: 10,
  fontSize: '0.85rem',
  outline: 'none',
};

const primaryBtn: React.CSSProperties = {
  background: 'var(--green)',
  color: '#fff',
  border: 'none',
  borderRadius: 999,
  padding: '0.45rem 1rem',
  fontSize: '0.8rem',
  fontWeight: 600,
  cursor: 'pointer',
};

const secondaryBtn: React.CSSProperties = {
  background: '#fff',
  color: 'var(--muted)',
  border: '1px solid var(--line)',
  borderRadius: 999,
  padding: '0.45rem 1rem',
  fontSize: '0.8rem',
  cursor: 'pointer',
};
